import { useLayoutEffect } from 'react'

// Client-side counterpart to layouts/_station_timezone.html.erb's blocking
// head script — same algorithm, same `data-tz-match` attribute, but run from
// React so it also fires on Inertia's client-side navigations, not just a
// full document load.
//
// The head script only runs once, when the document is first parsed. That's
// exactly right for that hard load (and is why it has to be a blocking
// script at all — see the ERB partial for the CLS story), but Inertia
// navigating from one station to another swaps the page via React without a
// new document, so the head script never runs again. Land on a page whose
// timezone happens to match your own by clicking into it from elsewhere in
// the SPA, and the suffix the head script would have hidden on a hard
// load/reload stays visible — the attribute set (or not set) by whichever
// page loaded the document is stale for every station visited after it.
//
// useLayoutEffect, not useEffect: it runs synchronously after DOM mutations
// but before the browser paints, which is the same "decide before first
// frame" property the head script exists for, just scoped to a client-side
// transition's own paint rather than the whole document's.
// visitorOffsetMinutesWest defaults to the real browser value; the parameter
// exists so tests can pin the "visitor" side without fighting the test
// runner's own process timezone (getTimezoneOffset isn't reliably mockable
// across Node/jsdom versions via TZ env changes at runtime).
export function stationMatchesVisitorTimezone(
  timezone,
  visitorOffsetMinutesWest = new Date().getTimezoneOffset(),
) {
  if (!timezone) return false
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date())
    const name = parts.find((p) => p.type === 'timeZoneName')?.value
    if (!name) return false
    let station = 0
    if (name !== 'GMT') {
      // "GMT+02:00", and "GMT+5:30" / "GMT+8" where the browser shortens it.
      const m = /^GMT([+-])(\d{1,2})(?::(\d{2}))?$/.exec(name)
      if (!m) return false
      station = (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] || 0))
    }
    // getTimezoneOffset counts minutes WEST of UTC; station is minutes east.
    return station === -visitorOffsetMinutesWest
  } catch {
    return false
  }
}

export function applyStationTimezoneMatch(timezone) {
  const html = document.documentElement
  if (stationMatchesVisitorTimezone(timezone)) {
    html.setAttribute('data-tz-match', '1')
  } else {
    // Must actively clear it: navigating from a matching station to a
    // non-matching one (or vice versa) leaves the previous page's attribute
    // on <html> otherwise — it's the same element across an Inertia
    // navigation, nothing removes it for us.
    html.removeAttribute('data-tz-match')
  }
}

export function useStationTimezoneMatch(timezone) {
  useLayoutEffect(() => {
    applyStationTimezoneMatch(timezone)
  }, [timezone])
}
